import { useState, useEffect, useCallback, useRef } from 'preact/hooks'
import type { WebTabKey } from '@/hooks/useWebTabRouter'
import { createPortal } from 'preact/compat'
import { Trash2, Settings, MoreHorizontal, Plus, Package, Sparkles, Globe, Loader2 } from 'lucide-preact'
import { websApi, type WebViewDto, type AssetPipelineMode } from '@/api/webs'
import { assetApi, type WebAssetListItemDto, type AssetQaSummaryDto } from '@/api/asset'
import { landingPagesApi } from '@/api/landingPages'
import type { LandingPageViewDto } from '@/types/landingPage'
import { AssetCreationPipeline } from '../AssetCreationPipeline'
import { AssetQaChip } from '../AssetQaChip'
import { CreateAssetModal } from '../modals/CreateAssetModal'
import { ExternalUrlModal } from '../modals/ExternalUrlModal'
import { ConfirmationModal } from '@/components/modals/ConfirmationModal'
import { BRAND_CLASSES } from '@/constants/theme'
import { DataGrid, type ColumnConfig } from '@/components/common/grids'
import { shortAgo } from '@/utils/time'
import { lowerSafe } from '@/utils/strings'
import { DRAFT_NOT_REVIEWABLE_MESSAGE, DRAFT_NOT_REVIEWABLE_TITLE } from '@/utils/permissionMessages'
import { useToast } from '@/hooks/useToast'
import { useAIChatContextUpdater } from '@/hooks'
import { useBulkEmailGeneration } from '@/hooks/useBulkEmailGeneration'
import { useSetupPipelineVisibility } from '@/hooks/useSetupPipelineVisibility'
import { useAssetQaVisible } from '@/hooks/useAssetQaVisible'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'
import { ASSET_TYPE_META, DEFAULT_ASSET_META, ASSET_STATUS_COLORS, isWebsterAssetType, getAssetTypeRank, compareAssetsByTypeThenName } from '../constants/assetMeta'
import { AssetReviewOverlay } from '../overlays/AssetReviewOverlay'
import { WebsterBuilderOverlay } from '../overlays/WebsterBuilderOverlay'
import type { WebsterAssetType } from '@/hooks/useWebsterBuilder'
import { loadEffectiveSettings, effectiveSettingsState, hasConfiguredSettings } from '@/state/workflowSettings'
import { useAssetReviewPermissions } from '@/hooks/useAssetReviewPermissions'
import { useAssetSearchFilter } from '@/hooks/useAssetSearchFilter'
import { refetchUnlessSentFieldsChanged, retryOnStaleETag } from '@/utils/etagRetry'

/** What a pipeline-mode flip sends, as the web holds it: a 412 is retried only when the mode did not change meanwhile. */
const savedPipelineMode = (document: WebViewDto) => document.AssetPipelineMode

/** A room web's recording rewrites the web in the background while it uploads and prepares (ORB-1087). */
const patchPipelineModeOnFreshWeb = (web: WebViewDto, mode: AssetPipelineMode) => retryOnStaleETag(
  document => websApi.patchAssetPipelineMode(web.WorkspaceId, web.Id, { ETag: document.ETag, Mode: mode }),
  web,
  refetchUnlessSentFieldsChanged(async () => (await websApi.getById(web.WorkspaceId, web.Id)).data ?? null, web, savedPipelineMode),
)

function getAssetIdFromUrl(): string | null {
  try {
    return new URLSearchParams(window.location.search).get('asset')
  } catch {
    return null
  }
}

function setAssetIdInUrl(assetId: string | null): void {
  try {
    const url = new URL(window.location.href)
    if (assetId) {
      url.searchParams.set('asset', assetId)
    } else {
      url.searchParams.delete('asset')
    }
    window.history.replaceState({}, '', url.toString())
  } catch {
    // SSR safety
  }
}

function AssetStatusBadge({ status }: { status: string }) {
  const colors = ASSET_STATUS_COLORS[status] || BRAND_CLASSES.STATUS.DEFAULT
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${colors}`}>
      {status}
    </span>
  )
}

// ORB-243: in-flight pill for assets currently being drafted by the bulk
// email-generation flow. Replaces the Draft badge until SignalR rebuilt lands.
function GeneratingBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
      <Loader2 className="w-3 h-3 animate-spin" />
      Generating…
    </span>
  )
}

/**
 * ORB-457: Type sorts by the fixed asset-type lifecycle rank, then by name within a type — an order
 * no single raw value can express, which is the only reason an override survives here. Every other
 * column carries an `accessor`, so those keys go back to the grid rather than being re-compared by
 * hand: the adapter that used to do that read every column's accessor and reimplemented, slightly
 * differently, the comparison DataGrid now derives itself (ORB-715).
 *
 * Module scope because it depends on nothing in the component — one stable identity, not a new
 * closure on every render.
 */
function assetSortComparator(key: string) {
  return key === 'type' ? compareAssetsByTypeThenName : undefined
}

interface Props {
  web: WebViewDto
  clientCompanyId: string
  workspaceId: string
  campaignId: string
  onPatchWeb: (patcher: (prev: WebViewDto) => WebViewDto) => void
  /** Routes to another tab of this web, for a QA gap whose fix is not on the asset (ORB-889). */
  routeToTab: (tab: WebTabKey) => void
  /** A builder may have saved a landing page draft, which the web's Sign Off list reads (ORB-914). */
  onBuilderClosed: () => void
}

export function WebAssetsTab({ web, clientCompanyId, workspaceId, campaignId, onPatchWeb, routeToTab, onBuilderClosed }: Props) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [assets, setAssets] = useState<WebAssetListItemDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()
  const [selectedAssetIndex, setSelectedAssetIndex] = useState<number | null>(null)
  const [assetToDelete, setAssetToDelete] = useState<WebAssetListItemDto | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [builderAsset, setBuilderAsset] = useState<{ id: string; type: WebsterAssetType } | null>(null)
  // ORB-457: client-side search + type filter over the already-loaded asset list (see hook).
  const { searchQuery, setSearchQuery, typeFilter, setTypeFilter, filteredAssets, availableTypes } = useAssetSearchFilter(assets)
  const { showSuccess, showError, showErrorUnless422, showInfo } = useToast()
  const { updateContext } = useAIChatContextUpdater()
  // Check if user is internal staff (for opening Webster builder)
  // canEditInWebster, not raw list membership: opening the builder is an edit, and a demoted
  // internal-team member keeps their list seat but not the right to change anything (ORB-666).
  const { canEditInWebster: canOpenBuilder, canCreateAsset, canDeleteAsset, settingsLoaded } =
    useAssetReviewPermissions(clientCompanyId, workspaceId)

  // Check if LP asset exists for this web
  const hasLandingPageAsset = assets.some(a => a.Type === 'Landing Page')
  const lpAsset = assets.find(a => a.Type === 'Landing Page')
  // Any asset that isn't the LP or the Email Template is an "AI-generated" email asset
  // (Email Copy variants from the bulk generator). Drives Step 3's completion + the
  // setup-pipeline auto-dismiss timer.
  const hasAiGeneratedAssets = assets.some(a => a.Type !== 'Landing Page' && a.Type !== 'Email Template')
  const isLpReadyOrSignedOff = lpAsset?.Status === 'Ready for Sign-Off' || lpAsset?.Status === 'Signed Off'
  const hasEmailTemplateAsset = assets.some(a => a.Type === 'Email Template')
  const emailTemplateAsset = assets.find(a => a.Type === 'Email Template')
  const isEmailReadyOrSignedOff = emailTemplateAsset?.Status === 'Ready for Sign-Off' || emailTemplateAsset?.Status === 'Signed Off'
  const existingAssetTypes = assets.map(a => a.Type)

  const handleOpenBuilder = useCallback((assetId: string, assetType: string) => {
    if (isWebsterAssetType(assetType)) {
      setBuilderAsset({ id: assetId, type: assetType as WebsterAssetType })
      setSelectedAssetIndex(null)
    }
  }, [])

  // When builder closes, transition directly to the review overlay so the user
  // can review what they just promoted. Draft assets have nothing reviewable
  // yet — return to the asset list instead of routing into the placeholder.
  // Keep the builder mounted until the review overlay is ready to avoid grid flash.
  const handleCloseBuilder = useCallback(() => {
    onBuilderClosed()
    const closingAssetId = builderAsset?.id ?? null
    if (!closingAssetId) {
      setBuilderAsset(null)
      return
    }
    const idx = assets.findIndex(a => a.Id === closingAssetId)
    if (idx >= 0 && assets[idx].Status === 'Draft') {
      // No version to review yet — exit cleanly back to the list.
      setBuilderAsset(null)
      loadAssets()
      return
    }
    if (idx >= 0) {
      setSelectedAssetIndex(idx)
      setAssetIdInUrl(closingAssetId)
      setBuilderAsset(null)
    } else {
      // Asset not in list yet (edge case) — fall back to pending flow
      setPendingReviewAssetId(closingAssetId)
    }
    // Refresh asset list in background
    loadAssets()
  }, [builderAsset?.id, assets, onBuilderClosed])

  // Pending review: once we have the asset ID and assets are loaded, open
  // the review overlay and THEN dismiss the builder in one batch. Draft assets
  // have no version to review — drop straight back to the asset list.
  const [pendingReviewAssetId, setPendingReviewAssetId] = useState<string | null>(null)
  useEffect(() => {
    if (!pendingReviewAssetId || loading) return
    const idx = assets.findIndex(a => a.Id === pendingReviewAssetId)
    if (idx >= 0 && assets[idx].Status !== 'Draft') {
      setSelectedAssetIndex(idx)
      setAssetIdInUrl(pendingReviewAssetId)
    }
    // Dismiss builder after review overlay state is set (same render batch)
    setBuilderAsset(null)
    setPendingReviewAssetId(null)
  }, [pendingReviewAssetId, loading, assets])

  useEffect(() => {
    updateContext({
      campaignId,
      webId: web.Id,
      location: ChatLocations.WebAssets,
      entityType: ChatEntityTypes.Web,
      entityId: web.Id,
      viewMode: ChatViewModes.View,
      availableFields: [],
      availableActions: ['CopyToClipboard'],
    })
  }, [workspaceId, campaignId, web.Id, updateContext])

  // ORB-243: `silent` skips the loading skeleton so the grid updates rows in
  // place — used for the per-asset refresh during bulk email generation.
  /**
   * What QA still owes, keyed by asset id (ORB-889).
   *
   * ⚑ One read for the whole tab, never one per row. Resolving each row's QA separately is the
   * per-row fan-out the console's queue forbids by name, and it would make the tab slower the busier
   * the web is — backwards, since a busy web is the one with something to show.
   */
  const [qaByAssetId, setQaByAssetId] = useState<Map<string, AssetQaSummaryDto>>(new Map())

  /**
   * ⚑⚑ Whether the QA read failed, tracked separately from having no results.
   *
   * Drawing no chips after a failed read would assert "nothing outstanding" about checks we never
   * received — a pass reported for something nobody performed, which is the one thing the rulebook's
   * honesty rule forbids and the reason this whole feature exists. An absent chip has to mean clean;
   * it must never mean unknown. So a failure says so instead.
   */
  const [qaUnavailable, setQaUnavailable] = useState(false)

  /**
   * ⚑ Whether this reader sees QA at all (ORB-982). Gates the read, not just the chips: a customer
   * should not be asking for findings they are not shown. With the read skipped the map stays empty
   * and `qaUnavailable` stays false, so neither the chips nor the unavailable notice can draw —
   * there is no second condition to keep in step with this one.
   */
  const qaVisible = useAssetQaVisible()

  // ⚑⚑ Read through a ref inside `loadAssets`, not from the closure (ORB-982). `loadAssets` is
  // re-created every render, but the memoized callbacks that call it — `handleCloseBuilder`,
  // `handleSubmitBypassUrl` — do not list it as a dependency, so they hold an older one and with it an
  // older flag. After a profile refresh revoked parent-company access, one of those could still issue
  // the QA read. The ref makes the gate apply when the read is issued rather than when the function
  // was created. Raised by Copilot on PR 243.
  const qaVisibleRef = useRef(qaVisible)
  useEffect(() => { qaVisibleRef.current = qaVisible }, [qaVisible])

  const loadAssets = async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true)
    setError(undefined)

    // Issued together, and the QA read is fetched HERE rather than in its own effect so that every
    // path that already refreshes this list — an upload, a delete, returning from the review overlay
    // after an override — keeps the chips in step without anyone remembering to add a trigger.
    const [response, qaResponse] = await Promise.all([
      assetApi.getWebAssetsByWeb(clientCompanyId, workspaceId, campaignId, web.Id),
      qaVisibleRef.current
        ? assetApi.getWebAssetQaSummary(clientCompanyId, workspaceId, campaignId, web.Id)
        : Promise.resolve(null),
    ])

    if (response.data) {
      // ORB-457: order by the fixed asset-type lifecycle rank, then name within a type.
      setAssets(response.data.slice().sort(compareAssetsByTypeThenName))
    } else if (response.error) {
      setError(response.error)
    }

    // A failed QA read costs the chips and not the list — this decorates assets that are useful
    // without it, and an error banner over them would charge the reader for a failure in something
    // they did not come for. But it is NOT silent: the map is left as it was rather than cleared,
    // and the tab says the QA state is unavailable. Clearing it would turn "we could not check" into
    // "nothing to see", which is the false pass this feature exists to prevent.
    // ⚑ A null qaResponse means the read was never issued because this reader does not see QA —
    // which is not a failure, and must not raise the unavailable notice.
    if (qaResponse === null) {
      setQaUnavailable(false)
    } else if (qaResponse.data) {
      setQaByAssetId(new Map(qaResponse.data.map(summary => [summary.AssetId, summary])))
      setQaUnavailable(false)
    } else {
      setQaUnavailable(true)
    }

    if (!options?.silent) setLoading(false)
  }

  // ⚑ qaVisible is a dependency, not decoration (ORB-982). `refreshUserProfile` can turn it on
  // without unmounting this tab, and without a reload the QA read is never issued for a session that
  // has just become entitled to it — the chips would stay absent until something else refreshed the
  // list. Raised by Copilot on PR 243.
  useEffect(() => {
    loadAssets()
  }, [clientCompanyId, workspaceId, campaignId, web.Id, qaVisible])

  // ORB-241: load the landing page so the pipeline can detect External-mode bypass
  const [landingPage, setLandingPage] = useState<LandingPageViewDto | null>(null)
  const reloadLandingPage = useCallback(async () => {
    const res = await landingPagesApi.getLandingPage(workspaceId, web.Id)
    if (res.data) setLandingPage(res.data)
    else if (res.status === 404) setLandingPage(null)
  }, [workspaceId, web.Id])
  useEffect(() => { reloadLandingPage() }, [reloadLandingPage])
  const lpExternalMode = landingPage?.Mode === 'External'

  // All three pipeline steps complete (LP signed off or external, Email Template signed off,
  // at least one AI asset). Drives both the celebration dwell and the post-wizard toolbar
  // simplification (kebab hides once the wizard is fully behind us).
  const allStepsComplete = (isLpReadyOrSignedOff || lpExternalMode) && isEmailReadyOrSignedOff && hasAiGeneratedAssets

  // Setup pipeline visibility — hides 5s after step 3 completes, persists per-Web in
  // localStorage, auto-clears the flag if state regresses below 3/3 so it reappears.
  const { hidden: pipelineDismissed } = useSetupPipelineVisibility({
    clientCompanyId,
    workspaceId,
    campaignId,
    webId: web.Id,
    allStepsComplete,
  })

  // ORB-241: bypass — let user supply their own URL instead of building an LP
  const [bypassOpen, setBypassOpen] = useState(false)

  const handleSubmitBypassUrl = useCallback(async (url: string) => {
    const res = await landingPagesApi.setMode(workspaceId, campaignId, web.Id, {
      Mode: 'External',
      ExternalUrl: url,
    })
    if (res.error || !res.data) {
      if (res.status !== 422) showError('Save failed', res.error || 'Unable to save external URL')
      return
    }
    setLandingPage(res.data)
    showSuccess('External URL set', 'Landing page now uses your custom URL.')
    setBypassOpen(false)
    loadAssets()
  }, [workspaceId, campaignId, web.Id, showError, showSuccess])

  // ORB-242: flip per-Web AssetPipelineMode (Strict | Free)
  const [pipelineModeFlipping, setPipelineModeFlipping] = useState(false)
  // Kebab menu — rendered through a portal because the toolbar lives inside
  // ancestors that clip absolute children when the page is short (Free mode
  // with no assets shows a tiny container; the menu would extend past it).
  const [pipelineMenuOpen, setPipelineMenuOpen] = useState(false)
  const [pipelineMenuRect, setPipelineMenuRect] = useState<{ top: number; right: number } | null>(null)
  const pipelineMenuButtonRef = useRef<HTMLButtonElement | null>(null)
  const openPipelineMenu = useCallback(() => {
    const rect = pipelineMenuButtonRef.current?.getBoundingClientRect()
    if (rect) setPipelineMenuRect({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
    setPipelineMenuOpen(true)
  }, [])
  const closePipelineMenu = useCallback(() => setPipelineMenuOpen(false), [])
  // Close on scroll/resize so the menu doesn't drift away from the kebab.
  useEffect(() => {
    if (!pipelineMenuOpen) return
    const close = () => closePipelineMenu()
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [pipelineMenuOpen, closePipelineMenu])
  const handleFlipPipelineMode = useCallback(async () => {
    if (pipelineModeFlipping) return
    const next: AssetPipelineMode = web.AssetPipelineMode === 'Free' ? 'Strict' : 'Free'
    setPipelineModeFlipping(true)
    const res = await patchPipelineModeOnFreshWeb(web, next)
    if (res.error || !res.data) {
      if (res.status !== 422) showError('Update failed', res.error || 'Unable to change pipeline mode')
    } else {
      onPatchWeb(() => res.data!)
      showSuccess(
        next === 'Free' ? 'Free-form mode' : 'Strict order',
        next === 'Free'
          ? 'All asset types are now unlocked. Create them in any order.'
          : 'Asset creation will follow the recommended order again.',
      )
    }
    setPipelineModeFlipping(false)
  }, [web, pipelineModeFlipping, onPatchWeb, showError, showSuccess])

  const isFreeMode = web.AssetPipelineMode === 'Free'

  // Auto-flip Strict → Free once the user has completed the 3-step pipeline and the
  // celebration dwell has dismissed it. The wizard's job is initial setup; once setup is
  // done, the toolbar's "+ Add asset" affordance should appear without making the user
  // hunt through the kebab menu. Ref-guarded so it fires at most once per Web per session
  // — if the user manually flips back to Strict mid-session, we don't undo their choice
  // (the ref stays set). A page reload would re-evaluate, which matches "once you're at
  // three, it's just free form."
  const autoFlipFiredRef = useRef<string | null>(null)
  const autoFlipInFlightRef = useRef(false)
  useEffect(() => {
    const key = `${web.WorkspaceId}:${web.Id}`
    if (autoFlipFiredRef.current === key) return
    if (isFreeMode) return
    if (!pipelineDismissed) return
    if (pipelineModeFlipping) return
    if (autoFlipInFlightRef.current) return
    autoFlipInFlightRef.current = true
    void (async () => {
      const res = await patchPipelineModeOnFreshWeb(web, 'Free')
      autoFlipInFlightRef.current = false
      if (res.data) {
        // Only latch after a successful PATCH so transient failures don't lock the
        // wizard in Strict mode for the rest of the session. The effect re-evaluates
        // on the next deps change (ETag, etc.), giving the auto-flip another shot.
        autoFlipFiredRef.current = key
        onPatchWeb(() => res.data!)
      } else if (res.status !== 422) {
        // Don't loudly toast a success path (no error to surface) or a refused
        // change (422) — the global validation toast reports it. Anything else means
        // the user is stuck in Strict despite finishing setup; point them at the
        // manual switch in the kebab.
        showError(
          'Couldn’t switch to free-form mode',
          res.error || 'Use the menu in the toolbar to switch manually.',
        )
      }
    })()
  }, [isFreeMode, pipelineDismissed, pipelineModeFlipping, web, onPatchWeb, showError])
  // Free-mode empty hero: show a centered Get-started card when the user has no assets yet
  // and hasn't set up an external LP. Avoids a near-empty page right after switching modes.
  const showFreeModeHero = isFreeMode && canCreateAsset && assets.length === 0 && !lpExternalMode && !loading

  // Quick-create LP asset and open builder directly (no modal)
  const [creatingLp, setCreatingLp] = useState(false)
  const handleQuickCreateLandingPage = useCallback(async () => {
    setCreatingLp(true)
    try {
      const response = await assetApi.createWebAssetWithoutFile(
        clientCompanyId, workspaceId, campaignId, web.Id,
        { Name: `${web.Name} Landing Page`, Type: 'Landing Page' }
      )
      if (response.error) {
        showErrorUnless422('Create failed', response)
        return
      }
      // Response data is the created asset — extract the ID.
      // Open builder immediately, refresh asset list in background.
      const created = response.data as { Id?: string } | null
      if (created?.Id) {
        setBuilderAsset({ id: created.Id, type: 'Landing Page' })
        loadAssets()
      }
    } catch {
      showError('Create failed', 'An unexpected error occurred.')
    } finally {
      setCreatingLp(false)
    }
  }, [clientCompanyId, workspaceId, campaignId, web.Id, web.Name, showError, showErrorUnless422, loadAssets])

  // ORB-243: bulk email-asset generation. Each asset's `rebuilt` event triggers
  // a silent refetch so the grid updates that row in place without a full
  // skeleton flash.
  const bulkGen = useBulkEmailGeneration({
    onAssetCompleted: () => loadAssets({ silent: true }),
    onGenerationError: (msg) => showError('Email generation failed', msg),
  })
  const [creatingAIAssets, setCreatingAIAssets] = useState(false)
  const handleGenerateAIAssets = useCallback(async () => {
    if (creatingAIAssets) return
    setCreatingAIAssets(true)
    try {
      const response = await assetApi.generateAllEmails(clientCompanyId, workspaceId, campaignId, web.Id)
      if (response.error || !response.data) {
        showErrorUnless422('Generation failed', response, 'Unable to start email generation.')
        return
      }
      const { Created, SkippedExisting } = response.data
      if (Created.length === 0 && SkippedExisting.length === 0) {
        // Catalog has no entries for this WebType (e.g. Demo/Download/Meeting today).
        showInfo('Nothing to generate', 'No email types are defined for this campaign type yet.')
        return
      }
      if (Created.length === 0) {
        showInfo('Already generated', 'All emails for this campaign have already been created.')
        return
      }
      // Only the queued ones will ever send `rebuilt`; tracking the rest would badge them forever.
      const queued = Created.filter(a => a.Queued)
      bulkGen.startTracking(queued)
      const skippedSuffix = SkippedExisting.length > 0
        ? ` ${SkippedExisting.length} already existed.`
        : ''
      const notQueued = Created.length - queued.length
      const notQueuedLine = `${notQueued} ${notQueued === 1 ? "wasn't" : "weren't"} started: open ${notQueued === 1 ? 'it' : 'them'} and ask Webster in the chat.`
      if (queued.length === 0) {
        showInfo('Emails created', `${Created.length} ${Created.length === 1 ? 'email' : 'emails'} added, but ${notQueuedLine}${skippedSuffix}`)
      } else {
        showSuccess(
          'Generating emails',
          `Drafting ${queued.length} ${queued.length === 1 ? 'email' : 'emails'}.${skippedSuffix}${notQueued > 0 ? ` ${notQueuedLine}` : ''}`,
        )
      }
      // Refetch so the new Draft rows appear; the badge will overlay them.
      loadAssets()
    } finally {
      setCreatingAIAssets(false)
    }
  }, [creatingAIAssets, clientCompanyId, workspaceId, campaignId, web.Id, bulkGen, showErrorUnless422, showInfo, showSuccess, loadAssets])

  // Quick-create Email Template asset and open builder directly
  const [creatingEmailTemplate, setCreatingEmailTemplate] = useState(false)
  const handleQuickCreateEmailTemplate = useCallback(async () => {
    setCreatingEmailTemplate(true)
    try {
      const response = await assetApi.createWebAssetWithoutFile(
        clientCompanyId, workspaceId, campaignId, web.Id,
        { Name: `${web.Name} Email Template`, Type: 'Email Template' }
      )
      if (response.error) {
        showErrorUnless422('Create failed', response)
        return
      }
      const created = response.data as { Id?: string } | null
      if (created?.Id) {
        setBuilderAsset({ id: created.Id, type: 'Email Template' })
        loadAssets()
      }
    } catch {
      showError('Create failed', 'An unexpected error occurred.')
    } finally {
      setCreatingEmailTemplate(false)
    }
  }, [clientCompanyId, workspaceId, campaignId, web.Id, web.Name, showError, loadAssets])

  // Deep-link: open overlay if ?asset= is present after assets load.
  //
  // Waits on effective settings too, not just the assets. canOpenBuilder is false until they land —
  // useAssetReviewPermissions fails closed — and settings load on their own effect, so an internal
  // Editor deep-linking to a Draft can arrive here before their permissions are known. That used to
  // be a silent no-op; now that the branch below says something, running early would tell an Editor
  // they cannot review their own draft (ORB-698).
  useEffect(() => {
    if (loading || assets.length === 0 || !settingsLoaded) return
    const assetId = getAssetIdFromUrl()
    if (!assetId) return
    const idx = assets.findIndex(a => a.Id === assetId)
    if (idx < 0) {
      setAssetIdInUrl(null)
      showError('Asset not found', 'The linked asset may have been deleted.')
      return
    }
    const asset = assets[idx]
    if (asset.Status === 'Draft') {
      setAssetIdInUrl(null)
      if (canOpenBuilder && isWebsterAssetType(asset.Type)) {
        handleOpenBuilder(asset.Id, asset.Type)
      } else {
        showInfo(DRAFT_NOT_REVIEWABLE_TITLE, DRAFT_NOT_REVIEWABLE_MESSAGE)
      }
      return
    }
    setSelectedAssetIndex(idx)
  }, [loading, assets, settingsLoaded, canOpenBuilder])

  // Refresh effective workflow settings every time the assets tab mounts
  useEffect(() => {
    loadEffectiveSettings(clientCompanyId, workspaceId, campaignId, web.Id)
  }, [clientCompanyId, workspaceId, campaignId, web.Id])

  const settingsLoading = effectiveSettingsState.value.loading
  const settingsConfigured = hasConfiguredSettings.value

  const columns: ColumnConfig<WebAssetListItemDto>[] = [
    {
      key: 'name',
      header: 'Asset Name',
      sortable: true,
      accessor: (asset) => lowerSafe(asset.Name),
      render: (asset) => {
        const meta = ASSET_TYPE_META[asset.Type] || DEFAULT_ASSET_META
        const Icon = meta.Icon
        const qa = qaByAssetId.get(asset.Id)
        return (
          <div className="flex items-center gap-2.5 min-w-0">
            <Icon className={`w-4 h-4 flex-shrink-0 ${meta.color}`} />
            <span className="font-medium text-primary-600 dark:text-primary-400 truncate">
              {asset.Name}
            </span>
            {/* Beside the name rather than in its own column: an asset with nothing outstanding
                shows nothing at all, and an empty column of mostly-blank cells reads as a rendering
                fault rather than as good news (ORB-889). */}
            {/* ⚑⚑ Gated on the flag as well as on the data, rather than relying on the map having
                started empty. The flag can flip to false while a populated map is still in state, and
                skipping the next read does nothing about chips already drawn from the last one. */}
            {qaVisible && qa && (
              <AssetQaChip
                openFindings={qa.OpenFindings}
                criticalFindings={qa.CriticalFindings}
              />
            )}
          </div>
        )
      },
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      // ORB-457: sort the Type column by the fixed lifecycle rank (getSortComparator
      // refines this with a secondary name sort), not alphabetically by label.
      accessor: (asset) => getAssetTypeRank(asset.Type),
      render: (asset) => (
        <span className="text-sm text-slate-600 dark:text-slate-400">{asset.Type}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      accessor: (asset) => asset.Status.toLowerCase(),
      // ORB-243: while a bulk-gen is in flight, the asset is still server-side
      // Status=Draft. Surface a "Generating…" pill instead so the user can see
      // it's actively being worked on.
      render: (asset) => bulkGen.isGenerating(asset.Id)
        ? <GeneratingBadge />
        : <AssetStatusBadge status={asset.Status} />,
    },
    {
      key: 'version',
      header: 'Version',
      sortable: true,
      accessor: (asset) => asset.CurrentVersionNumber,
      render: (asset) => (
        <span className="text-sm text-slate-600 dark:text-slate-400">
          v{asset.CurrentVersionNumber}
        </span>
      ),
    },
    {
      key: 'lastUpdated',
      header: 'Last Updated',
      sortable: true,
      accessor: (asset) => asset.UpdatedAt ? new Date(asset.UpdatedAt).getTime() : 0,
      render: (asset) => {
        if (!asset.UpdatedAt) return <span className="text-sm text-slate-400">—</span>
        return (
          <div>
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {shortAgo(asset.UpdatedAt)}
            </div>
            {asset.UpdatedBy && (
              <div className="text-xs text-slate-400 dark:text-slate-500">
                by {asset.UpdatedBy}
              </div>
            )}
          </div>
        )
      },
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      // The side panel disables delete once an asset is signed off; the grid did not, so the same
      // asset was deletable from one surface and not the other (ORB-666).
      render: (asset) => canDeleteAsset && asset.Status !== 'Signed Off' ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setAssetToDelete(asset)
          }}
          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          title="Delete asset"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      ) : null,
    },
  ]

  return (
    <div>
      {/* Settings not configured banner */}
      {!settingsLoading && !settingsConfigured && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/20 p-4">
          <Settings className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              No default reviewers configured
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-400/80 mt-0.5">
              Add at least one default reviewer in the Settings tab to enable the asset approval workflow.
            </p>
          </div>
        </div>
      )}

      {/* Asset toolbar — ORB-242: kebab menu for mode switch + LP bypass.
          Strict mode: kebab offers "Switch to Free-form" so users can opt out of the wizard.
          Free mode pre-completion: kebab offers "Switch to Strict order" so users can opt
          back into the wizard if they jumped to Free early.
          Free mode post-completion (the auto-flip outcome): the wizard is fully behind us,
          so both flip options are meaningless and the kebab hides entirely. The LP bypass
          stays in either mode until an LP exists. */}
      {!loading && (() => {
        // Asset creation + workflow controls are internal-only — hide the whole
        // toolbar (Add asset button + workflow kebab) from non-internal users.
        if (!canCreateAsset) return null
        const showLpBypass = !hasLandingPageAsset && !lpExternalMode
        const showSwitchToFree = !isFreeMode
        const showSwitchToStrict = isFreeMode && !allStepsComplete
        const kebabHasItems = showSwitchToFree || showSwitchToStrict || showLpBypass
        return (
        <div className={`flex items-center mb-3 ${isFreeMode && !showFreeModeHero ? 'justify-between' : 'justify-end'}`}>
          {isFreeMode && !showFreeModeHero && (
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" /> Add asset
            </button>
          )}
          {kebabHasItems && (
            <button
              ref={pipelineMenuButtonRef}
              type="button"
              onClick={() => (pipelineMenuOpen ? closePipelineMenu() : openPipelineMenu())}
              disabled={pipelineModeFlipping}
              title="Workflow options"
              className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          )}
          {pipelineMenuOpen && pipelineMenuRect && createPortal(
            <>
              <div className="fixed inset-0 z-[60]" onClick={closePipelineMenu} />
              <div
                className="fixed w-64 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-[61]"
                style={{ top: `${pipelineMenuRect.top}px`, right: `${pipelineMenuRect.right}px` }}
              >
                <div className="py-1">
                  {(showSwitchToFree || showSwitchToStrict) && (
                    <button
                      type="button"
                      onClick={() => { closePipelineMenu(); handleFlipPipelineMode() }}
                      className="flex items-start gap-3 w-full px-4 py-2.5 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                    >
                      <Sparkles className="w-4 h-4 mt-0.5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium">
                          {showSwitchToFree ? 'Switch to Free-form' : 'Switch to Strict order'}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {showSwitchToFree
                            ? 'Unlock all asset types and add them in any order.'
                            : 'Follow the recommended LP → Email → Other order.'}
                        </div>
                      </div>
                    </button>
                  )}
                  {showLpBypass && (
                    <button
                      type="button"
                      onClick={() => { closePipelineMenu(); setBypassOpen(true) }}
                      className="flex items-start gap-3 w-full px-4 py-2.5 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 border-t border-slate-100 dark:border-slate-700"
                    >
                      <Globe className="w-4 h-4 mt-0.5 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium">Use my own URL for landing page</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          Skip the AI builder and supply an external URL.
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            </>,
            document.body,
          )}
        </div>
        )
      })()}

      {/* Free-mode empty hero — ORB-242: nicer landing surface than a near-empty page */}
      {showFreeModeHero && (
        <div className="flex flex-col items-center justify-center py-16 text-center rounded-xl border border-dashed border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30">
          <div className="p-4 rounded-full bg-primary-50 dark:bg-primary-900/20 mb-4">
            <Sparkles className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
            Get started
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mb-6">
            You're in Free-form mode. Add assets in any order — there's no required sequence.
          </p>
          <button
            type="button"
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add asset
          </button>
        </div>
      )}

      {/* Non-internal users can't create assets — show a clean read-only empty
          state instead of the creation hero/pipeline when there's nothing here yet.
          Once an asset exists, the grid takes over (this no longer renders). */}
      {!canCreateAsset && !loading && assets.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center rounded-xl border border-dashed border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30">
          <div className="p-4 rounded-full bg-slate-100 dark:bg-slate-800 mb-4">
            <Package className="w-8 h-8 text-slate-400 dark:text-slate-500" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
            No assets yet
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md">
            Assets will appear here once they've been created.
          </p>
        </div>
      )}

      {/* Asset creation pipeline — Strict mode only. Free mode hides it entirely; users add via the toolbar above.
          Once all three steps complete, the pipeline self-dismisses after a short celebration dwell (see useSetupPipelineVisibility). */}
      {!loading && canCreateAsset && !isFreeMode && !pipelineDismissed && (
        <AssetCreationPipeline
          hasLandingPageAsset={hasLandingPageAsset}
          isLpReadyOrSignedOff={isLpReadyOrSignedOff}
          hasEmailTemplateAsset={hasEmailTemplateAsset}
          isEmailReadyOrSignedOff={isEmailReadyOrSignedOff}
          lpExternalMode={lpExternalMode}
          lpExternalUrl={lpExternalMode ? landingPage?.DeployedUrl ?? null : null}
          onCreateLandingPage={handleQuickCreateLandingPage}
          onCreateEmailTemplate={handleQuickCreateEmailTemplate}
          onCreateAIAssets={handleGenerateAIAssets}
          onBypassLandingPage={() => setBypassOpen(true)}
          creatingLp={creatingLp}
          creatingEmailTemplate={creatingEmailTemplate}
          creatingAIAssets={creatingAIAssets}
          aiAssetsInFlight={bulkGen.inFlightCount}
          hasAiGeneratedAssets={hasAiGeneratedAssets}
        />
      )}

      <ExternalUrlModal
        isOpen={bypassOpen}
        onClose={() => setBypassOpen(false)}
        onSubmit={handleSubmitBypassUrl}
        title="Use your own URL"
        description="Skip the AI builder and point this campaign at a landing page you host elsewhere. Stats, registrations, and email links will still target this URL."
        submitLabel="Save URL"
      />

      {/* The load error rides above the grid rather than through DataGrid's `error` prop, which
          replaces the table with a dead end offering no way back (the call UserManagement and
          BlastRecipientsSection both made). Outside the grid block deliberately: a failed first load
          leaves no assets, so the block does not render and an error inside it would never be seen. */}
      {error && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

      {/* ⚑ Says so rather than showing nothing. An absent chip means the asset is clean; it must
          never also mean "we could not find out", or the list quietly reports a pass on checks that
          were never read — the one thing the rulebook's honesty rule forbids (ORB-889).
          Muted rather than an error: the assets themselves loaded, and that is what the reader came
          for. */}
      {qaVisible && qaUnavailable && (
        <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
          Quality check state could not be loaded, so it is not shown here. Open an asset to see its
          checks.
        </p>
      )}

      {/* Asset Grid */}
      {!creatingLp && !creatingEmailTemplate && (loading || assets.length > 0 || hasLandingPageAsset) && (
        <DataGrid
          title="Assets"
          data={filteredAssets}
          columns={columns}
          getRowKey={(asset) => asset.Id}
          isLoading={loading}
          getSortComparator={assetSortComparator}
          defaultSortKey="type"
          defaultSortDirection="asc"
          emptyMessage={assets.length > 0 ? 'No assets match your search or filter.' : 'No assets yet. Create your first asset to get started.'}
          pagination={true}
          pageSize={10}
          // ORB-457's search and type filter, now in the grid's own header strip. Both are offered
          // only once there is something to filter, and both stay put when a query narrows the list
          // to empty, so the reader can always clear what they typed.
          search={
            assets.length > 0
              ? {
                  value: searchQuery,
                  onChange: setSearchQuery,
                  placeholder: 'Search assets...',
                  ariaLabel: 'Search assets by name',
                }
              : undefined
          }
          toolbarActions={
            assets.length > 0 ? (
              // The Filter icon that used to sit beside this did not come with it: at toolbar scale
              // it reads as a second control rather than a label for the one next to it.
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.currentTarget.value)}
                aria-label="Filter assets by type"
                className="rounded border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:focus-visible:ring-offset-slate-800"
              >
                <option value="all">All types</option>
                {availableTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            ) : undefined
          }
          // Every value that narrows the list, not just the search: two type filters can match the
          // same number of rows, and page 3 of one is not page 3 of the other.
          pageResetKey={`${searchQuery}|${typeFilter}`}
          onRowClick={(asset) => {
            if (asset.Status === 'Draft') {
              // Drafts have no version yet — only internal staff can open the builder. Everyone else
              // gets told why the row does nothing, which is the same reason either way: there is no
              // version to review. Also covers a Draft of a type the builder does not handle (ORB-698).
              if (canOpenBuilder && isWebsterAssetType(asset.Type)) {
                handleOpenBuilder(asset.Id, asset.Type)
              } else if (settingsLoaded) {
                // Nothing is claimed until the settings that decide canOpenBuilder have landed. The
                // grid renders before they do, so an early click would otherwise tell an Editor they
                // cannot review their own draft.
                showInfo(DRAFT_NOT_REVIEWABLE_TITLE, DRAFT_NOT_REVIEWABLE_MESSAGE)
              }
              return
            }
            const idx = assets.findIndex(a => a.Id === asset.Id)
            if (idx >= 0) {
              setSelectedAssetIndex(idx)
              setAssetIdInUrl(asset.Id)
            }
          }}
        />
      )}

      {/* Create Asset Modal */}
      {isCreateModalOpen && (
        <CreateAssetModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          clientCompanyId={clientCompanyId}
          workspaceId={workspaceId}
          campaignId={campaignId}
          webId={web.Id}
          webType={web.Type}
          existingAssetTypes={existingAssetTypes}
          onAssetCreated={(created) => {
            setIsCreateModalOpen(false)
            // Webster types come back with an id + type — open the builder directly
            // so the user can start editing (mirrors strict-mode quick-create).
            // File-upload assets have no builder; just refresh the grid.
            if (created) setBuilderAsset({ id: created.id, type: created.type })
            loadAssets()
          }}
        />
      )}

      {/* Asset Review Overlay */}
      {selectedAssetIndex !== null && (
        <AssetReviewOverlay
          assets={assets}
          initialIndex={selectedAssetIndex}
          clientCompanyId={clientCompanyId}
          workspaceId={workspaceId}
          campaignId={campaignId}
          onClose={() => {
            setSelectedAssetIndex(null)
            setAssetIdInUrl(null)
          }}
          onAssetChange={(assetId) => setAssetIdInUrl(assetId)}
          onAssetUpdated={loadAssets}
          onEditInWebster={handleOpenBuilder}
          // Closes the overlay first: the reader is being sent to another tab of the web
          // underneath it, and leaving it open lands them on the right screen behind a
          // full-bleed asset viewer (ORB-889).
          onGoToWebTab={(tab) => {
            setSelectedAssetIndex(null)
            setAssetIdInUrl(null)
            routeToTab(tab)
          }}
        />
      )}

      {/* Webster Builder Overlay */}
      {builderAsset && (
        <WebsterBuilderOverlay
          clientCompanyId={clientCompanyId}
          workspaceId={workspaceId}
          campaignId={campaignId}
          webId={web.Id}
          assetId={builderAsset.id}
          assetType={builderAsset.type}
          onClose={handleCloseBuilder}
          onAssetUpdated={loadAssets}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={assetToDelete !== null}
        title="Delete Asset"
        message={`Are you sure you want to delete '${assetToDelete?.Name}'? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
        loading={isDeleting}
        onCancel={() => setAssetToDelete(null)}
        onConfirm={async () => {
          if (!assetToDelete) return
          setIsDeleting(true)
          const response = await assetApi.deleteAsset(clientCompanyId, workspaceId, campaignId, assetToDelete.Id)
          if (response.error) {
            showErrorUnless422('Delete failed', response)
          } else {
            showSuccess('Asset deleted', `${assetToDelete.Name} has been deleted.`)
            loadAssets()
          }
          setIsDeleting(false)
          setAssetToDelete(null)
        }}
      />
    </div>
  )
}
